import React, { useEffect, useState, useRef } from 'react';
import type { User, PathwayBlock, BlockUpload } from '../../../types';
import { DataService } from '../../../services/firebase';
import { useTranslation } from 'react-i18next';
import { Upload, CheckCircle, XCircle, Clock, FileText, Image, Film, File } from 'lucide-react';

interface UploadBlockViewProps {
  user: User;
  block: PathwayBlock;
  pathwayId: string;
}

const MIME_MAP: Record<string, string> = {
  image: 'image/*',
  pdf: 'application/pdf',
  document: '.doc,.docx,.odt,.txt,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  video: 'video/*',
};

const FileIcon: React.FC<{ type: string; className?: string }> = ({ type, className }) => {
  if (type.startsWith('image/')) return <Image className={className} />;
  if (type.startsWith('video/')) return <Film className={className} />;
  if (type.includes('pdf')) return <FileText className={className} />;
  return <File className={className} />;
};

export const UploadBlockView: React.FC<UploadBlockViewProps> = ({ user, block, pathwayId }) => {
  const { t } = useTranslation();
  const [uploadRecord, setUploadRecord] = useState<BlockUpload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const config = block.uploadConfig;
  const instructions = config?.instructions || block.uploadInstructions || '';
  const acceptedTypes = config?.acceptedFileTypes || [];
  const requiresVerification = config?.requiresAdminVerification ?? true;

  useEffect(() => {
    const load = async () => {
      try {
        const record = await DataService.getBlockUpload(user.id, pathwayId, block.id);
        setUploadRecord(record);

        if (record?.status === 'approved' || (!requiresVerification && record)) {
          await DataService.setBlockProgress({
            candidateId: user.id,
            pathwayId,
            blockId: block.id,
            status: 'complete',
            completedAt: record.reviewedAt || record.uploadedAt,
          });
        }
      } catch (e) {
        console.error('Failed to load upload record', e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [user.id, block.id, pathwayId]);

  const acceptString = acceptedTypes.map(t => MIME_MAP[t] || '').filter(Boolean).join(',');

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadProgress(0);
    try {
      const storagePath = `uploads/${user.activeOrgId}/${pathwayId}/${block.id}/${user.id}/${file.name}`;
      // Simulate progress since uploadBytes doesn't support progress
      setUploadProgress(50);
      const fileUrl = await DataService.uploadBlockFile(file, storagePath);
      setUploadProgress(90);

      const status = requiresVerification ? 'pending_review' as const : 'approved' as const;
      const record: Omit<BlockUpload, 'id'> = {
        candidateId: user.id,
        pathwayId,
        blockId: block.id,
        fileUrl,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        uploadedAt: Date.now(),
        status,
      };

      const saved = await DataService.saveBlockUpload(record);
      setUploadRecord(saved);

      if (!requiresVerification) {
        await DataService.setBlockProgress({
          candidateId: user.id,
          pathwayId,
          blockId: block.id,
          status: 'complete',
          completedAt: Date.now(),
        });
      }
    } catch (e) {
      console.error('Upload failed', e);
    } finally {
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const canUpload = !uploadRecord || uploadRecord.status === 'rejected';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aero-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-2xl mx-auto space-y-4">
      {/* Block header */}
      <div className="bg-white rounded-xl border border-aero-200 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-aero-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Upload className="w-5 h-5 text-aero-600" />
          </div>
          <div>
            <p className="text-xs font-bold text-aero-400 uppercase tracking-wide mb-1">{t('candidate.block.upload.title')}</p>
            <h2 className="text-lg font-bold text-aero-900">{block.name}</h2>
            {block.description && <p className="text-sm text-aero-600 mt-1">{block.description}</p>}
          </div>
        </div>
      </div>

      {/* Instructions */}
      {instructions && (
        <div className="bg-aero-50 rounded-xl border border-aero-200 p-4">
          <p className="text-xs font-semibold text-aero-600 mb-1">{t('candidate.block.upload.instructions')}</p>
          <p className="text-sm text-aero-700">{instructions}</p>
        </div>
      )}

      {/* Accepted file types */}
      {acceptedTypes.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {acceptedTypes.map(ft => (
            <span key={ft} className="text-[10px] font-bold uppercase px-2.5 py-1 rounded-full bg-aero-100 text-aero-600">
              {ft}
            </span>
          ))}
        </div>
      )}

      {/* Upload status */}
      {uploadRecord && (
        <div className={`rounded-xl border-2 p-5 ${
          uploadRecord.status === 'approved' ? 'bg-green-50 border-green-200' :
          uploadRecord.status === 'rejected' ? 'bg-red-50 border-red-200' :
          'bg-yellow-50 border-yellow-200'
        }`}>
          <div className="flex items-start gap-3">
            {uploadRecord.status === 'approved' ? (
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            ) : uploadRecord.status === 'rejected' ? (
              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            ) : (
              <Clock className="w-5 h-5 text-yellow-500 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <FileIcon type={uploadRecord.fileType} className="w-4 h-4 text-aero-400" />
                <p className="text-sm font-semibold text-aero-900 truncate">{uploadRecord.fileName}</p>
              </div>
              <p className="text-xs text-aero-500">
                {(uploadRecord.fileSize / 1024).toFixed(0)} KB · {new Date(uploadRecord.uploadedAt).toLocaleDateString()}
              </p>

              {uploadRecord.status === 'approved' && (
                <p className="text-xs text-green-600 font-semibold mt-1">{t('candidate.block.upload.approved')}</p>
              )}
              {uploadRecord.status === 'pending_review' && (
                <p className="text-xs text-yellow-600 font-semibold mt-1">{t('candidate.block.upload.pendingReview')}</p>
              )}
              {uploadRecord.status === 'rejected' && (
                <>
                  <p className="text-xs text-red-600 font-semibold mt-1">{t('candidate.block.upload.rejected')}</p>
                  {uploadRecord.rejectionReason && (
                    <p className="text-xs text-red-500 mt-0.5 italic">"{uploadRecord.rejectionReason}"</p>
                  )}
                </>
              )}

              {/* Preview for images */}
              {uploadRecord.fileType.startsWith('image/') && (
                <img src={uploadRecord.fileUrl} alt="" className="mt-3 max-h-48 rounded-lg border border-aero-200 object-contain" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload progress */}
      {uploadProgress !== null && (
        <div className="bg-white rounded-xl border border-aero-200 p-4">
          <div className="h-2 bg-aero-100 rounded-full overflow-hidden">
            <div className="h-full bg-aero-600 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
          </div>
          <p className="text-xs text-aero-500 text-center mt-2">{t('candidate.block.upload.uploading')}</p>
        </div>
      )}

      {/* Upload button */}
      {canUpload && uploadProgress === null && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptString}
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-aero-600 hover:bg-aero-700 text-white rounded-xl font-semibold text-sm transition-colors shadow-sm"
          >
            <Upload className="w-5 h-5" />
            {uploadRecord?.status === 'rejected'
              ? t('candidate.block.upload.reupload')
              : t('candidate.block.upload.selectFile')}
          </button>
        </>
      )}
    </div>
  );
};
